// =============================================================================
// Direct Messages & Presence
// =============================================================================

/**
 * Direct Messaging tuning constants. Values marked [TUNING] are initial
 * hypotheses that should be revisited against real telemetry (churn,
 * abuse reports, gameplay session length) after the first few weeks of
 * production traffic.
 */
export const DM = {
  /** Max characters per message body (Zod + Mongoose schema enforce).
   *  Casual word-game DMs rarely exceed a sentence — 300 chars is typical
   *  for game chat and keeps push-notification previews manageable.
   *  [TUNING] — revisit after launch based on p95 message length. */
  MAX_MESSAGE_LENGTH: 300,
  /** Cursor page size for conversation + message history queries. */
  HISTORY_PAGE_SIZE: 30,
  /** Messages auto-purge after this many days via Mongoose TTL index.
   *  [TUNING] — 90d balances cloud storage cost against user expectation;
   *  reassess if retention becomes a support-ticket pattern. */
  RETENTION_DAYS: 90,
  /** Client-side idle timeout before `dm:typing_stop` is emitted (ms), i.e.
   *  how long after the last keystroke the peer's "typing…" indicator stays
   *  up. NOT a debounce on `dm:typing_start` — that event is edge-triggered
   *  in ChatThreadScreen (one emit per typing burst) because the per-socket
   *  budget is only READ_RATE_LIMIT_PER_MINUTE. */
  TYPING_DEBOUNCE_MS: 1500,
  /** Per-user send rate limit. Max sends per 60-second window; further sends
   *  are rejected with DM_RATE_LIMITED by the Redis quota in redisState.
   *  Tuned for a word-game context where DMs are secondary to gameplay —
   *  12/min is roughly one every 5 s, ample for rapid replies but tight
   *  enough that push fan-out to offline recipients stays manageable.
   *  [TUNING] — monitor rejected-send rate + user complaints. */
  SEND_RATE_LIMIT_PER_MINUTE: 12,
  /** Per-user list/history fetch rate limit. */
  LIST_RATE_LIMIT_PER_MINUTE: 60,
  /** Per-user moderation actions (block/unblock/report) rate limit. */
  MODERATION_RATE_LIMIT_PER_MINUTE: 10,
  /** Per-socket rate limit for dm:read and dm:typing_* events. */
  READ_RATE_LIMIT_PER_MINUTE: 60,
  /** Per-socket rate limit for dm:like toggles (#530). A double-tap toggles
   *  one like; 60/min is ample for rapid tapping while bounding the fan-out
   *  fired at both participants per toggle. */
  LIKE_RATE_LIMIT_PER_MINUTE: 60,
  /** Max characters for the optional notes field on a MessageReport. */
  REPORT_NOTES_MAX_LENGTH: 500,
  /** Max characters for `Conversation.lastMessagePreview` — short enough to
   *  render on the inbox row in one line, long enough to be useful. Shared
   *  by the Mongoose schema (maxlength), messagingService.sendMessage
   *  (truncation on $set), and the mobile messagingStore reducer so the
   *  three call sites can't drift. */
  PREVIEW_MAX_LENGTH: 120,
  /** Page size for GET /api/messaging/blocks — caps unbounded queries for
   *  users with long block histories. Callers that need more can paginate. */
  BLOCKS_PAGE_SIZE: 100,
  /** Per-user daily cap on reports across all messages. Stops abuse-of-
   *  reporting attacks where a single account tries to flood the moderation
   *  queue. Enforced via a Redis counter that resets every 24h.
   *  [TUNING] — 25 is a hypothesis; validate with real report volumes. */
  REPORTS_PER_USER_PER_DAY: 25,
} as const;

/**
 * DM content-moderation tuning. The profanity filter runs server-side in
 * `moderationService.filter()` and has ONE outcome for a deny-list hit: mask
 * the offending word and deliver the rest of the message. It never rejects a
 * message for its content.
 */
export const MODERATION = {
  /** Replacement rendered in place of a deny-listed word. Fixed-width so
   *  the masked length doesn't leak the original word's length. */
  MASK_TOKEN: '****',
  /** Minimum canonical token length eligible for the whole-message "collapsed"
   *  evasion pass (catches "s i k i ş" / "a.m.c.i"). Short tokens are excluded
   *  from this pass to avoid cross-word substring false positives. */
  COLLAPSED_MIN_TOKEN_LENGTH: 6,
  /** ModerationEvent rows (the flagged-message audit log) auto-purge after this
   *  many days via a Mongoose TTL index — same horizon as message retention. */
  FLAG_RETENTION_DAYS: 90,
  /** Admin moderation queue page sizes. */
  REPORTS_PAGE_SIZE: 25,
  FLAGS_PAGE_SIZE: 25,
  /** Number of messages fetched on each side of a reported message for the
   *  admin "conversation context" view. */
  CONTEXT_WINDOW: 10,
} as const;

/**
 * Post-match quick chat (match-end screen). Players exchange PREDEFINED
 * messages only — the wire carries a stable message id, never free text, so
 * there is nothing to sanitize or moderate. Each client renders the id in its
 * OWN UI language via the mobile i18n files (`quickChat.messages.*`).
 */
export const QUICK_CHAT = {
  /**
   * Stable wire ids. The server Zod schema validates incoming sends against
   * this exact list, and the mobile i18n files carry one display string per
   * id (en + tr). NEVER rename or remove an id — old clients may still send
   * it; retire an id by dropping its chip from the mobile UI only.
   */
  MESSAGE_IDS: [
    'good_game',
    'well_played',
    'so_lucky',
    'on_fire',
    'brain_lag',
    'no_mercy',
    'that_hurt',
    'next_time',
    'rematch_taunt',
    'emoji_laugh',
    'emoji_cry',
    'emoji_mind_blown',
  ] as const,
  /** Per-socket send budget. Quick chat is a burst surface (a couple of
   *  taunts after a match) — 10/min is ample and bounds spam fan-out. */
  SEND_RATE_LIMIT_PER_MINUTE: 10,
  /** How long after `Match.endedAt` sends are still accepted. Bounds the
   *  surface to the match-end screen dwell time so an old matchId can't be
   *  used as a permanent unsolicited-message channel to a stranger. */
  POST_MATCH_WINDOW_MS: 10 * 60_000,
  /** Max entries kept in the client-side store feed (older entries drop off).
   *  The UI shows only the latest message per side as a transient avatar
   *  bubble — the store cap just bounds memory. */
  FEED_MAX_ENTRIES: 8,
  /** Avatar-bubble fade-in duration (ms). */
  BUBBLE_IN_MS: 150,
  /** How long an avatar bubble stays fully visible before fading (ms). */
  BUBBLE_VISIBLE_MS: 2600,
  /** Avatar-bubble fade-out duration (ms). */
  BUBBLE_OUT_MS: 300,
} as const;

/** One of the predefined quick-chat message ids. */
export type QuickChatMessageId = (typeof QUICK_CHAT.MESSAGE_IDS)[number];

/**
 * In-match quick chat (#792): predefined lines and emoji a player can send to
 * the whole table WHILE the match is running — the social channel for a long
 * Batak / King / Okey / backgammon match. Separate from {@link QUICK_CHAT}
 * (post-match, 1v1 only): different list, different window, every seat.
 */
export const IN_MATCH_CHAT = {
  /**
   * Stable wire ids — same contract as `QUICK_CHAT.MESSAGE_IDS`: the server
   * Zod enum validates against this list, the mobile i18n carries one string
   * per text id (en + tr) and one glyph per `emoji_*` id. NEVER rename or
   * remove an id; retire one by dropping it from the picker only.
   */
  MESSAGE_IDS: [
    'hello',
    'good_luck',
    'well_played',
    'nice_move',
    'thanks',
    'sorry',
    'hurry_up',
    'oops',
    'so_lucky',
    'good_game',
    'emoji_laugh',
    'emoji_cool',
    'emoji_thumbs_up',
    'emoji_clap',
    'emoji_heart',
    'emoji_thinking',
    'emoji_angry',
    'emoji_cry',
  ] as const,
  /** Per-user send budget (socket window AND Redis quota). A line every
   *  few seconds is conversation; more is spam fanned out to three seats. */
  SEND_RATE_LIMIT_PER_MINUTE: 8,
  /** How long a line stays up as a bubble at the sender's seat (client). */
  BUBBLE_DISPLAY_MS: 4_000,
} as const;

export type InMatchChatMessageId = (typeof IN_MATCH_CHAT.MESSAGE_IDS)[number];

export const PRESENCE = {
  /** Socket heartbeat cadence used to refresh the presence TTL (seconds). */
  HEARTBEAT_SECONDS: 30,
  /** Extra grace before a missed heartbeat flips presence to offline (seconds). */
  OFFLINE_GRACE_SECONDS: 30,
} as const;

export const FRIENDS = {
  /** TTL applied to an invite-link token in Redis. Long enough that a user
   *  can share a link over SMS/iMessage/etc. and let the recipient tap it
   *  at their leisure, short enough that an abandoned link eventually
   *  evaporates instead of piling up in Redis forever. [TUNING]. */
  INVITE_TOKEN_TTL_SECONDS: 24 * 60 * 60,
  // NOTE: the invite-token Redis key prefix used to live here. It moved to
  // `framework/server/src/config/redisKeys.ts` as the GAME-scoped
  // `INVITE_TOKEN_PREFIX` (#611) — a key defined in `shared/` is one key for
  // every game AND invisible to the chokepoint guard. Do NOT re-add it here;
  // a surviving constant is a re-armable trap.
  /** UUID v4 shape. Used at the REST boundary as defence-in-depth so a
   *  non-UUID token never reaches the Redis key lookup (Zod is the
   *  primary guard; this covers anything that bypasses the body parser). */
  INVITE_TOKEN_REGEX:
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
} as const;

/**
 * Placeholder prefix the mobile client uses when it opens a DM thread
 * before the server has created a real Conversation row (i.e., no
 * messages exchanged yet). Shared so the store guards, the ChatThread
 * selector, and the FriendsScreen row renderer compare against exactly
 * the same literal.
 */
export const PENDING_CONVERSATION_PREFIX = 'pending-';
